/**
 * Remote Access (AnyDesk) Service — "ที่เก็บเดียว แก้ได้จากหลายหน้าจอ" (เจ้าของโปรเจกต์ 23 ก.ย. 2026)
 *
 * ความจริงอยู่ที่ `rental_items.anydesk_id` ของ "รอบเช่า" (เริ่มใหม่ทุกสัญญา) — ทุกหน้าจอแก้ค่าเดียวกัน
 * ผ่าน endpoint ของ backend (สิทธิ์ `rentals.remote_access` ทุกเส้น · backend มี audit):
 *  - หน้ารายละเอียดการเช่า → PUT /rentals/{rental_id}/items/{item_id}/remote-access
 *  - หน้าอุปกรณ์ → GET/PUT /devices/{id}/remote-access (หา rental item ของสัญญาที่เครื่องอยู่ให้เอง)
 *  - หน้าเคสซ่อม → PUT /device-maintenance/{id}/remote-access
 *
 * body: key ที่ไม่ส่ง = ไม่แตะ · null/'' = ล้างค่า · backend ตัดช่องว่างใน AnyDesk ID ให้เอง (สูงสุด 50 ตัว)
 * ไม่มี "รหัสผ่าน AnyDesk" อีกแล้ว (PO 2026-09-07)
 */
import ApiWithLogging from '../utils/apiWithLogging';

export const REMOTE_ACCESS_PERMISSION = 'rentals.remote_access';
export const ANYDESK_ID_MAX_LENGTH = 50;

export interface RemoteAccessUpdate {
  anydesk_id?: string | null;
  remote_access_notes?: string | null;
}

export interface RentalItemRemoteAccessResponse {
  item_id: number;
  rental_id: number;
  anydesk_id: string | null;
  remote_access_notes: string | null;
  changed: boolean;
}

export interface DeviceRemoteAccess {
  anydesk_id: string | null;
  remote_access_notes: string | null;
  rental_item_id: number | null;
  rental_id: number | null;
  rental_number: string | null;
}

export const remoteAccessService = {
  /** แก้ AnyDesk ของรายการเช่า (ความจริงชุดเดียว) */
  async updateRentalItem(
    rentalId: number,
    itemId: number,
    data: RemoteAccessUpdate,
  ): Promise<RentalItemRemoteAccessResponse> {
    return ApiWithLogging.put<RentalItemRemoteAccessResponse>(
      `/rentals/${rentalId}/items/${itemId}/remote-access`,
      data,
      {},
      {
        service: 'remoteAccessService',
        action: 'rental_item_remote_access_update',
        entityType: 'rental',
        entityId: rentalId,
        details: { item_id: itemId },
      },
    );
  },

  /** AnyDesk ปัจจุบันของเครื่อง (จากสัญญาที่เครื่องอยู่ — ทุกค่า null ถ้าไม่อยู่ในสัญญา) */
  async getDevice(deviceId: number): Promise<DeviceRemoteAccess> {
    return ApiWithLogging.get<DeviceRemoteAccess>(`/devices/${deviceId}/remote-access`);
  },

  /** แก้ AnyDesk ผ่านเครื่อง → backend เขียนลงรายการเช่าของสัญญาที่เครื่องอยู่ (400 ถ้าไม่อยู่ในสัญญา) */
  async updateDevice(deviceId: number, data: RemoteAccessUpdate): Promise<DeviceRemoteAccess> {
    return ApiWithLogging.put<DeviceRemoteAccess>(`/devices/${deviceId}/remote-access`, data, {}, {
      service: 'remoteAccessService',
      action: 'device_remote_access_update',
      entityType: 'device',
      entityId: deviceId,
    });
  },

  /** แก้ AnyDesk จากหน้าเคสซ่อม → backend เขียนลงรายการเช่าของเคส */
  async updateMaintenance(maintenanceId: number, data: RemoteAccessUpdate): Promise<any> {
    return ApiWithLogging.put<any>(`/device-maintenance/${maintenanceId}/remote-access`, data, {}, {
      service: 'remoteAccessService',
      action: 'maintenance_remote_access_update',
      entityType: 'device_maintenance',
      entityId: maintenanceId,
    });
  },
};

export default remoteAccessService;
